import React, { useState, useEffect, useCallback } from 'react';

const NoInternet = () => {
    const [isChecking, setIsChecking] = useState(false);
    const [lastChecked, setLastChecked] = useState(null);

    const handleRetry = useCallback(() => {
        setIsChecking(true);
        setLastChecked(null);
        setTimeout(() => {
            setIsChecking(false);
            setLastChecked(new Date());
            if (navigator.onLine) {
                window.location.reload();
            }
        }, 1500);
    }, []);

    useEffect(() => {
        const handleOnline = () => window.location.reload();
        window.addEventListener('online', handleOnline);
        return () => window.removeEventListener('online', handleOnline);
    }, []);

    return (
        <section className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
            <div className="max-w-md w-full text-center">
                {/* Animated wifi-off icon */}
                <div className="mx-auto mb-8 relative w-36 h-36">
                    {/* Pulsing rings */}
                    <div className="absolute inset-0 rounded-full bg-amber-100 animate-ping opacity-30" />
                    <div className="absolute inset-4 rounded-full bg-amber-100 animate-ping opacity-40"
                        style={{ animationDelay: '0.3s' }} />
                    <div className="relative w-36 h-36 rounded-full bg-amber-50 border-2 border-amber-200 flex items-center justify-center shadow-md">
                        <svg className="w-16 h-16 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                                d="M3 3l18 18M8.111 8.111A8.95 8.95 0 005.07 10.07M15.89 8.111A8.95 8.95 0 0118.93 10.07M10.586 10.586A4.48 4.48 0 0012 10.5c.83 0 1.606.225 2.272.615M12 19h.01" />
                        </svg>
                    </div>
                </div>

                <h1 className="text-3xl font-bold text-gray-900 mb-3">No Internet Connection</h1>
                <p className="text-gray-500 mb-2 leading-relaxed">
                    It looks like you're offline. Please check your network connection and try again.
                </p>
                {lastChecked && (
                    <p className="text-xs text-gray-400 mb-6">
                        Last checked: {lastChecked.toLocaleTimeString()}
                    </p>
                )}
                {!lastChecked && <div className="mb-6" />}

                {/* Status chips */}
                <div className="flex justify-center gap-3 mb-8 flex-wrap">
                    <div className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 rounded-full text-xs text-red-600 font-medium">
                        <div className="w-2 h-2 rounded-full bg-red-400" />
                        Offline
                    </div>
                    <div className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 rounded-full text-xs text-gray-500 font-medium">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Auto-reconnecting...
                    </div>
                </div>

                <button
                    id="no-internet-retry-btn"
                    onClick={handleRetry}
                    disabled={isChecking}
                    className="bg-primary hover:bg-primary-hover px-8 py-2.5 text-white rounded-lg text-sm font-medium transition-all shadow-xs disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2 mx-auto"
                >
                    {isChecking ? (
                        <>
                            <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                <path className="opacity-75" fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                            </svg>
                            Checking...
                        </>
                    ) : (
                        <>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                            Try Again
                        </>
                    )}
                </button>

                <p className="mt-6 text-xs text-gray-400">
                    We'll automatically reconnect once your network is back.
                </p>
            </div>
        </section>
    );
};

export default NoInternet;
